declare var defaultApiConfig: any;
declare var API: {
  url: string;
  /** Same builder API as `url`, but `builderApi/{websiteUuid}` instead of `builderApi/{websiteId}`. */
  urlUuid?: string;
  urlV2?: string;
  backendURL: string | null;
};

import { captureFetchApiFailure } from "../sentry";

/** Thrown for non-OK JSON responses; includes HTTP status and parsed body when available. */
export class FetchApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "FetchApiError";
    this.status = status;
    this.body = body;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Builder v2 base when `websiteUuid` is available; falls back to v1. */
function getBuilderApiBase(): string {
  return API.urlV2 ?? API.url;
}

/** Same builder API version as `url`; uses `builderApi/{uuid}` when `API.urlUuid` is set. */
function getBuilderApiUuidBase(): string {
  return API.urlUuid ?? API.url;
}

function fetchWithBase<TResponse>(
  base: string,
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  const newConfig = {
    ...defaultApiConfig,
    ...config,
    headers: { ...defaultApiConfig.headers, ...config.headers },
  };

  if (!jsonType) {
    return fetch(`${base}${url}`, newConfig).then(
      (data) => data as unknown as TResponse
    );
  }
  return fetch(`${base}${url}`, newConfig).then(async (response) => {
    if (response.status === 204 || response.status === 205) {
      return undefined as TResponse;
    }

    const text = await response.text();

    if (!text || text.trim() === "") {
      if (!response.ok) {
        captureFetchApiFailure(response.status, `${base}${url}`, undefined);
        throw new FetchApiError(
          `Request failed: ${response.status}`,
          response.status,
          undefined
        );
      }
      return undefined as TResponse;
    }

    let parsed: unknown = text;
    try {
      parsed = JSON.parse(text);
    } catch (_error) {
      parsed = text;
    }

    if (!response.ok) {
      captureFetchApiFailure(response.status, `${base}${url}`, parsed);

      const message =
        typeof parsed === "object" && parsed && "message" in parsed
          ? String((parsed as { message?: unknown }).message)
          : `Request failed: ${response.status}`;
      throw new FetchApiError(message, response.status, parsed);
    }

    return parsed as TResponse;
  });
}

export function fetchT<TResponse>(
  url: string,
  config: RequestInit = {}
): Promise<TResponse> {
  const newConfig = {
    ...defaultApiConfig,
    ...config,
    headers: { ...defaultApiConfig.headers, ...config.headers },
  };

  return fetch(url, newConfig)
    .then((response) => response.json())
    .then((data) => data as TResponse);
}

export function fetchApi<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  return fetchWithBase<TResponse>(API.url, url, config, jsonType);
}

/** Same paths as v1; uses `builderApi/v2/{uuid}` when `API.urlV2` is set. */
export function fetchApiV2<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  return fetchWithBase<TResponse>(getBuilderApiBase(), url, config, jsonType);
}

export function fetchApiGet<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, getHeaders(params));
}

export function fetchApiUuid<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  return fetchWithBase<TResponse>(
    getBuilderApiUuidBase(),
    url,
    config,
    jsonType
  );
}

export function fetchApiGetUuid<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApiUuid(url, getHeaders(params));
}

export function fetchApiGetV2<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApiV2(url, getHeaders(params));
}

export function fetchApiPost<TResponse>(
  url: string,
  params: {} = {},
  jsonType: boolean = true
): Promise<TResponse> {
  return fetchApi(url, postHeaders(params), jsonType);
}

export function fetchApiPostV2<TResponse>(
  url: string,
  params: {} = {},
  jsonType: boolean = true
): Promise<TResponse> {
  return fetchApiV2(url, postHeaders(params), jsonType);
}

export function fetchApiPut<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, putHeaders(params));
}

export function fetchApiPutV2<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApiV2(url, putHeaders(params));
}

export function fetchApiDelete<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, deleteHeaders());
}

export function fetchApiDeleteV2<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApiV2(url, deleteHeaders());
}

export const getHeaders = (params: {} = {}) => {
  return {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    params: params,
  };
};

export const postHeaders = (payload: any) => {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
};

export const postFormHeaders = (payload: any) => {
  return {
    method: "POST",
    body: payload,
  };
};

const putHeaders = (payload: any) => {
  return {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
};

const deleteHeaders = () => {
  return {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  };
};

// function saveVersion() {
//   return 'API: Save version.'
// }

// function loadVersion() {
//   return fetchApiGet(`/load`, getHeaders())
//     .then((res: any) => res.json())
//     .then((result) => result)
// }

export default API;
